import { v4 as uuid } from "uuid";

// Same shape for every transform builder (data source mappings, computed fields)
export type TransformTestResult = {
  id: string;
  success: boolean;
  value: string | null;
  unchanged: boolean;
  error?: string;
  logs: string[];
};

// The JSON object arguments a test case passes to the transform function
export type TestCaseFormat = {
  args: string[];
  // May be missing or null, e.g. history when an employee is created
  optionalArgs?: string[];
};

// Test cases are edited as raw JSON text, parsed only when running
export type TestCase = {
  id: string;
  name: string;
  json: string;
};

export type ParsedTestCase = { id: string; name: string } & Record<
  string,
  Record<string, unknown> | null | undefined | string
>;

const isObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

export const describeFormat = (format: TestCaseFormat) =>
  `{ ${[...format.args, ...(format.optionalArgs ?? [])].join(", ")} }`;

export const emptyTestCaseJson = (format: TestCaseFormat) =>
  JSON.stringify(
    Object.fromEntries([...format.args, ...(format.optionalArgs ?? [])].map(arg => [arg, {}])),
    null,
    2
  );

export const newTestCase = (name: string, json: string): TestCase => ({ id: uuid(), name, json });

export const toTestCase = (
  format: TestCaseFormat,
  parsed: { id?: string; name: string } & Record<string, unknown>
): TestCase => ({
  id: parsed.id ?? uuid(),
  name: parsed.name,
  json: JSON.stringify(
    Object.fromEntries(
      [...format.args, ...(format.optionalArgs ?? [])]
        .filter(arg => parsed[arg] !== undefined)
        .map(arg => [arg, parsed[arg]])
    ),
    null,
    2
  )
});

const missingArgsError = (format: TestCaseFormat) => {
  const required = format.args.map(a => `"${a}"`).join(" and ");
  const optional = format.optionalArgs?.length
    ? ` (optionally ${format.optionalArgs.map(a => `"${a}"`).join(", ")})`
    : "";
  return `must be an object with ${required} object${format.args.length > 1 ? "s" : ""}${optional}`;
};

// Returns the arguments of a case, or undefined when the entry does not match the format
const pickArgs = (format: TestCaseFormat, entry: unknown) => {
  if (!isObject(entry)) return undefined;
  if (!format.args.every(arg => isObject(entry[arg]))) return undefined;

  const optional = format.optionalArgs ?? [];
  if (
    !optional.every(arg => entry[arg] === undefined || entry[arg] === null || isObject(entry[arg]))
  )
    return undefined;

  return Object.fromEntries(
    [...format.args, ...optional]
      .filter(arg => entry[arg] !== undefined)
      .map(arg => [arg, entry[arg] as Record<string, unknown> | null])
  );
};

export const parseTestCase = (format: TestCaseFormat, testCase: TestCase): ParsedTestCase => {
  const args = pickArgs(format, JSON.parse(testCase.json));
  if (!args) throw new Error(`Test case "${testCase.name}" ${missingArgsError(format)}`);
  return { ...args, id: testCase.id, name: testCase.name };
};

// Accepts a single case object or an array of them, optionally with names
export const parseUploadedTestCases = (
  format: TestCaseFormat,
  content: string,
  fileName: string
): TestCase[] => {
  const parsed = JSON.parse(content);
  const entries: unknown[] = Array.isArray(parsed) ? parsed : [parsed];

  return entries.map((entry, i) => {
    const args = pickArgs(format, entry);
    if (!args) throw new Error(`Entry ${i + 1} ${missingArgsError(format)}`);

    const name =
      isObject(entry) && typeof entry.name === "string"
        ? entry.name
        : entries.length > 1
        ? `${fileName} #${i + 1}`
        : fileName;
    return toTestCase(format, { ...args, name });
  });
};
